import Cookies from 'js-cookie';
import { action, computed, makeObservable, observable, reaction, runInAction, when } from 'mobx';
import moment from 'moment';

import {
    filterUrlQuery,
    getAccountType,
    getTrustedDomainName,
    isCryptocurrency,
    LocalStore,
    removeCookies,
    routes,
    SessionStore,
    urlForLanguage,
} from '@deriv/shared';
import { getInitialLanguage, localize } from '@deriv-com/translations';

import { requestRestLogout, WS } from 'Services';
import { fetchAccounts, fetchOTP } from '../Services/accounts-api';
import {
    clearTokens,
    ensureValidAccessToken,
    generateOAuthURL,
    getStoredToken,
    isOAuthBearerToken,
} from '../Services/oauth';

import { getClientAccountType } from './Helpers/client';
import { buildCurrenciesList } from './Modules/Trading/Helpers/currency';
import BaseStore from './base-store';

import BinarySocket from '_common/base/socket_base';
import { getRegion, isMultipliersOnly, isOptionsBlocked } from '_common/utility';

/** OAuth2 bearers from dbotspace (ory_at_…) must use the v4 OTP socket, not v3 `{ authorize }`. */
const isOAuthStyleBearer = token => {
    if (!token || typeof token !== 'string') return false;
    if (token.startsWith('ory_at_')) return true;
    try {
        return sessionStorage.getItem('auth_mode') === 'v2';
    } catch {
        return false;
    }
};

const LANGUAGE_KEY = 'i18n_language';
const storage_key = 'current_account';
const store_name = 'client_store';

export default class ClientStore extends BaseStore {
    loginid;
    preferred_language;
    email;
    user_id;
    external_id;

    current_account = null;
    initialized_broadcast = false;
    is_authorize = false;
    is_logging_in = false;
    is_client_store_initialized = false;
    has_logged_out = false;
    should_redirect_user_to_login = false;
    is_new_session = false;

    currencies_list = {};
    selected_currency = '';

    has_cookie_account = false;
    tab_visibility_handler = null;

    constructor(root_store) {
        const local_storage_properties = [];
        super({ root_store, local_storage_properties, store_name });

        makeObservable(this, {
            loginid: observable,
            preferred_language: observable,
            email: observable,
            user_id: observable,
            current_account: observable,
            initialized_broadcast: observable,
            currencies_list: observable,
            selected_currency: observable,
            is_authorize: observable,
            is_logging_in: observable,
            is_client_store_initialized: observable,
            has_logged_out: observable,
            should_redirect_user_to_login: observable,
            has_cookie_account: observable,
            is_new_session: observable,

            balance: computed,
            currency: computed,
            is_virtual: computed,
            is_eu: computed,
            is_logged_in: computed,
            account_type: computed,
            default_currency: computed,
            residence: computed,
            email_address: computed,
            landing_company_shortcode: computed,

            is_cr_account: computed,
            is_mf_account: computed,
            is_options_blocked: computed,
            is_multipliers_only: computed,

            has_active_real_account: computed,
            has_any_real_account: computed,
            is_single_currency: computed,

            setPreferredLanguage: action.bound,
            setCookieAccount: action.bound,
            responsePayoutCurrencies: action.bound,
            responseAuthorize: action.bound,
            setLoginId: action.bound,
            setIsAuthorize: action.bound,
            setIsLoggingIn: action.bound,
            setBalanceActiveAccount: action.bound,
            selectCurrency: action.bound,
            setEmail: action.bound,
            setInitialized: action.bound,
            setIsClientStoreInitialized: action.bound,
            cleanUp: action.bound,
            logout: action.bound,
            setLogout: action.bound,
            setShouldRedirectToLogin: action.bound,
            init: action.bound,
            resetVirtualBalance: action.bound,
            is_crypto: action.bound,
            switchAccount: action.bound,
        });

        reaction(
            () => [this.is_logged_in, this.loginid, this.email, this.currency, this.preferred_language],
            () => {
                this.setCookieAccount();
            }
        );

        when(
            () => !this.is_logged_in && this.root_store.ui && this.root_store.ui.is_real_acc_signup_on,
            () => this.root_store.ui.closeRealAccountSignup()
        );
    }

    get balance() {
        if (this.current_account?.balance !== undefined && this.current_account?.balance !== null) {
            return this.current_account.balance.toString();
        }
        return undefined;
    }

    get has_active_real_account() {
        return !this.is_virtual;
    }

    get has_any_real_account() {
        return !this.is_virtual;
    }

    get currency() {
        if (this.selected_currency.length) {
            return this.selected_currency;
        }
        return this.current_account?.currency || this.default_currency;
    }

    is_crypto(currency) {
        return isCryptocurrency(currency || this.currency);
    }

    get default_currency() {
        if (Object.keys(this.currencies_list).length > 0) {
            const keys = Object.keys(this.currencies_list);
            if (this.currencies_list[localize('Fiat')]?.length < 1) return 'USD';
            return Object.values(this.currencies_list[`${keys[0]}`])[0].text;
        }
        return 'USD';
    }

    get is_logged_in() {
        const hasToken = !!getStoredToken();
        const hasCurrentAccountLoginId = !!this.current_account?.loginid;
        const hasLoginId = !!this.loginid;
        return hasToken && hasCurrentAccountLoginId && hasLoginId;
    }

    get is_virtual() {
        // Reference loginid to make this computed reactive to account switches
        this.loginid;
        return getAccountType() === 'demo';
    }

    get is_eu() {
        return this.landing_company_shortcode === 'maltainvest';
    }

    get account_type() {
        return getClientAccountType(this.loginid);
    }

    get residence() {
        return this.current_account?.residence || '';
    }

    get email_address() {
        return this.current_account?.email || '';
    }

    get landing_company_shortcode() {
        // Default to 'svg' for ROW behavior (maximum permissiveness)
        return this.current_account?.landing_company_shortcode || 'svg';
    }

    get is_cr_account() {
        return this.loginid?.startsWith('CR');
    }

    get is_mf_account() {
        return this.loginid?.startsWith('MF');
    }

    get is_options_blocked() {
        return isOptionsBlocked(this.residence);
    }

    get is_multipliers_only() {
        return isMultipliersOnly(this.residence);
    }

    get is_single_currency() {
        return true; // Simplified for single account
    }

    setIsAuthorize(value) {
        this.is_authorize = value;
    }

    setPreferredLanguage = lang => {
        this.preferred_language = lang;
        LocalStore.setObject(LANGUAGE_KEY, lang);
    };

    setCookieAccount() {
        const domain = getTrustedDomainName();

        const { loginid, landing_company_shortcode, currency, preferred_language, user_id } = this;
        const email = this.email;
        const residence = this.residence;

        if (loginid && email) {
            const client_information = {
                loginid,
                email,
                landing_company_shortcode,
                currency,
                residence,
                preferred_language,
                user_id,
            };
            Cookies.set('region', getRegion(landing_company_shortcode, residence), { domain });
            Cookies.set('client_information', client_information, { domain });
            this.has_cookie_account = true;
        } else {
            removeCookies('region', 'client_information');
            this.has_cookie_account = false;
        }
    }

    responsePayoutCurrencies(response) {
        // Since payout_currencies endpoint has been removed, use USD as fallback
        const list = response?.payout_currencies || response || ['USD'];
        this.currencies_list = buildCurrenciesList(Array.isArray(list) ? list : ['USD']);
        this.selectCurrency('');
    }

    responseAuthorize(response) {
        const { authorize } = response;

        runInAction(() => {
            this.current_account = {
                loginid: authorize.loginid,
                balance: authorize.balance,
                currency: authorize.currency,
                email: authorize.email || '',
                landing_company_shortcode: authorize.landing_company_name || '',
                residence: authorize.country || '',
                session_start: parseInt(moment().utc().valueOf() / 1000),
            };

            this.setLoginId(authorize.loginid);
        });

        this.user_id = authorize.user_id || '';
        if (this.user_id) {
            localStorage.setItem('active_user_id', this.user_id);
        }

        // Store active_loginid for backward compatibility with notification system and multi-tab sync
        localStorage.setItem('active_loginid', authorize.loginid);
        sessionStorage.setItem('active_loginid', authorize.loginid);

        // Store current account
        localStorage.setItem(storage_key, JSON.stringify(this.current_account));
    }

    async resetVirtualBalance() {
        this.root_store.notifications.removeNotificationByKey({ key: 'reset_virtual_balance' });
        this.root_store.notifications.removeNotificationMessage({
            key: 'reset_virtual_balance',
            should_show_again: true,
        });
        await WS.authorized.topupVirtual();
    }

    isAccountOfType = type => {
        const client_account_type = getClientAccountType(this.loginid);
        return (
            (type === 'virtual' && client_account_type === 'virtual') ||
            (type === 'real' && client_account_type !== 'virtual') ||
            type === client_account_type
        );
    };

    async init(external_id) {
        // Remove any legacy token parameters from URL
        this.removeTokenFromUrl();

        let search = '';
        try {
            search = SessionStore?.get?.('signup_query_param') || window?.location?.search || '';
        } catch (error) {
            search = '';
        }

        const search_params = new URLSearchParams(search);
        const redirect_url = search_params?.get('redirect_url');
        const action_param = search_params?.get('action');
        const loginid_param = search_params?.get('loginid');

        // Refresh expired access tokens before init — avoids half-dead sessions
        // (0.00 balance / empty account dropdown) until the user clears storage.
        const valid_token = await ensureValidAccessToken();

        if (valid_token) {
            // Set is_logging_in to true while we wait for authorization
            this.setIsLoggingIn(true);

            const is_embedded = typeof window !== 'undefined' && window.__DTRADER_IS_EMBEDDED;

            if (is_embedded) {
                const token = getStoredToken() || valid_token;
                try {
                    if (isOAuthBearerToken(token)) {
                        // dbotspace / Kicktrades parents pass OAuth bearers (ory_at_…).
                        // v3 `{ authorize: oauth }` returns InputValidationFailed — use v4 OTP
                        // (same path as standalone and the parent bot's working WS).
                        await this.initEmbedOAuthV4();
                    } else {
                        // Legacy per-account API token in token1 / accountsList.
                        await this.initEmbedAuth(token);
                    }
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error('[Auth] Embed-mode auth failed:', error);
                    // Keep parent OAuth in storage — do NOT open public WS (destroys OTP session).
                    window.__DTRADER_EMBED_AUTH_PENDING = false;
                    try {
                        window.parent.postMessage(
                            { type: 'DTRADER_REQUEST_AUTH', source: 'dtrader-embed' },
                            '*'
                        );
                    } catch (_) {
                        /* ignore */
                    }
                }
            } else {
                // ----------------------------------------------------------------
                // Standalone mode: the v4 OAuth flow as before
                // ----------------------------------------------------------------
                // Step 5 of OAuth flow: fetch accounts → pick active account → get
                // OTP WS URL. The OTP URL embeds auth — once the socket opens and
                // subscribes to balance, socket-general.js calls authorizeAccount()
                // which completes the login.
                try {
                    const accounts = await fetchAccounts();
                    const active_account =
                        accounts.find(a => a.account_id === sessionStorage.getItem('active_loginid')) ||
                        accounts.find(a => a.account_type === 'demo') ||
                        accounts[0];

                    if (!active_account) throw new Error('No accounts found');

                    sessionStorage.setItem('active_loginid', active_account.account_id);
                    localStorage.setItem('active_loginid', active_account.account_id);
                    localStorage.setItem('account_type', active_account.account_type);

                    const ws_url = await fetchOTP(active_account.account_id);
                    BinarySocket.setWSUrl(ws_url);
                    BinarySocket.closeAndOpenNewConnection();

                    // Wait for balance response which serves as authorization.
                    // socket-general.js processes the balance response and calls authorizeAccount().
                    await BinarySocket.wait('balance');
                } catch (error) {
                    // eslint-disable-next-line no-console
                    console.error('[Auth] Account init failed:', error);
                    clearTokens();
                    // Same public-WS fallback as embed mode so the user still
                    // gets market data even when their session can't be restored.
                    try {
                        BinarySocket.setWSUrl(null);
                        BinarySocket.closeAndOpenNewConnection();
                    } catch (_) {
                        // ignore
                    }
                }
            }
        }

        // Handle special action parameters and user_id for both logged-in and logged-out states
        if (
            ['crypto_transactions_withdraw', 'payment_withdraw', 'payment_agent_withdraw'].includes(action_param) &&
            loginid_param
        ) {
            this.setLoginId(loginid_param);
        } else {
            this.setLoginId(window.sessionStorage.getItem('active_loginid') || LocalStore.get('active_loginid'));
        }

        this.user_id = LocalStore.get('active_user_id');

        // Load current account from localStorage if not already set
        if (!this.current_account) {
            const stored_account = LocalStore.getObject(storage_key);
            if (stored_account) {
                this.current_account = stored_account;
            }
        }

        this.external_id = external_id;

        // Handle redirect and language settings for logged-in users
        if (this.is_logged_in) {
            if (redirect_url) {
                const redirect_route = routes[redirect_url].length > 1 ? routes[redirect_url] : '';
                const has_action = [
                    'crypto_transactions_withdraw',
                    'payment_agent_withdraw',
                    'payment_withdraw',
                    'reset_password',
                ].includes(action_param);

                if (has_action) {
                    const query_string = filterUrlQuery(search, ['platform', 'code', 'action', 'loginid']);
                    window.location.replace(`${redirect_route}/redirect?${query_string}`);
                } else {
                    window.location.replace(`${redirect_route}/?${filterUrlQuery(search, ['platform', 'lang'])}`);
                }
            }

            const language = this.current_account?.preferred_language || getInitialLanguage();
            const stored_language_without_double_quotes = LocalStore.get(LANGUAGE_KEY).replace(/"/g, '');
            if (stored_language_without_double_quotes && language !== stored_language_without_double_quotes) {
                window.history.replaceState({}, document.title, urlForLanguage(language));
                await this.root_store.common.changeSelectedLanguage(language);
            }
        }

        this.selectCurrency('');

        // Since payout_currencies endpoint has been removed, use USD as default
        this.responsePayoutCurrencies(['USD']);

        this.setIsLoggingIn(false);
        this.setInitialized(true);

        // Clean up URL parameters
        if (action_param === 'signup') {
            const filteredQuery = filterUrlQuery(search, ['lang']);
            history.replaceState(
                null,
                null,
                window.location.href.replace(`${search}`, filteredQuery === '' ? '' : `?${filteredQuery}`)
            );
        }

        this.setIsClientStoreInitialized();

        // Set up visibility change listener to check whoami when tab becomes visible
        this.setupVisibilityListener();

        return true;
    }

    /**
     * Sets up a visibility change listener: when the tab becomes visible again,
     * refresh an expired access token. Only redirect to OAuth if refresh fails.
     */
    setupVisibilityListener() {
        this.removeVisibilityListener();

        this.tab_visibility_handler = () => {
            if (document.visibilityState !== 'visible') return;

            ensureValidAccessToken().then(token => {
                if (token) return;
                // Session unrecoverable (no/invalid refresh token). If we still
                // look logged in from leftover account state, force re-login.
                if (this.loginid || this.current_account?.loginid) {
                    generateOAuthURL().then(url => window.location.replace(url));
                }
            });
        };

        document.addEventListener('visibilitychange', this.tab_visibility_handler);
    }

    /**
     * Removes the visibility change listener
     */
    removeVisibilityListener() {
        if (this.tab_visibility_handler) {
            document.removeEventListener('visibilitychange', this.tab_visibility_handler);
            this.tab_visibility_handler = null;
        }
    }

    /**
     * Embed-mode authorize handshake.
     *
     * Connects to the legacy v3 WebSocket — the same protocol the parent
     * (Kicktrades / Traderkit) and TATE itself use — and sends a single
     * `{ authorize: <token> }` message. Deriv's v3 WS accepts both OAuth2
     * bearers and per-account API tokens in this field, which is why this
     * path works with whatever the parent's v1 surface already has on hand.
     *
     * Steps:
     *   1. Build the v3 URL using the host-supplied app_id (config.app_id is
     *      written by the embed bridge in index.html from the parent payload).
     *   2. Set `window.__DTRADER_EMBED_AUTH_PENDING = true` so socket_base.js
     *      skips its auto-subscribeBalance — that auto-subscribe would race
     *      our `authorize` and the server would reply AuthorizationRequired,
     *      tripping the `InvalidToken` path in socket-general.js.
     *   3. Open the v3 socket. deriv-api buffers the `authorize` send below
     *      until the WS open event flushes the queue — so it is ALWAYS sent
     *      first, ahead of any subsequent calls.
     *   4. After authorize succeeds, populate the client store, mark the
     *      session authorized, and subscribe to balance ourselves.
     */
    /**
     * v4 OTP embed: socket_base skips auto balance while embed flags are set — request it explicitly.
     */
    async waitForEmbedBalance(timeoutMs = 45000) {
        if (this.is_authorize && this.root_store?.common?.is_socket_opened) {
            try {
                BinarySocket.send({ balance: 1, subscribe: 1 });
            } catch (_) {
                /* ignore */
            }
            return;
        }

        try {
            BinarySocket.send({ balance: 1, subscribe: 1 });
        } catch (_) {
            /* deriv-api buffers until socket open */
        }
        await Promise.race([
            BinarySocket.wait('balance'),
            new Promise((resolve, reject) => {
                setTimeout(() => {
                    if (this.is_authorize) {
                        resolve();
                        return;
                    }
                    reject(new Error('Embed OTP balance wait timed out'));
                }, timeoutMs);
            }),
        ]);
    }

    /**
     * Embed + OAuth bearer: v4 accounts → OTP signed WebSocket (matches dbotspace parent).
     */
    async initEmbedOAuthV4() {
        const preferred_loginid =
            sessionStorage.getItem('active_loginid') || localStorage.getItem('active_loginid') || '';

        const socket_open = this.root_store?.common?.is_socket_opened;
        // Skip redundant OTP reconnect only when WS is actually up for this loginid.
        if (
            this.is_authorize &&
            this.loginid &&
            preferred_loginid &&
            this.loginid === preferred_loginid &&
            socket_open
        ) {
            return;
        }

        if (window.__DTRADER_EMBED_OTP_BUSY && window.__DTRADER_EMBED_OTP_PROMISE) {
            return window.__DTRADER_EMBED_OTP_PROMISE;
        }

        window.__DTRADER_EMBED_OTP_BUSY = true;
        window.__DTRADER_EMBED_AUTH_PENDING = true;
        window.__DTRADER_EMBED_MODE_ACTIVE = true;

        const reconnectEmbedOtp = async () => {
            if (window.__DTRADER_EMBED_OTP_BUSY) {
                return;
            }
            if (this.is_authorize && this.root_store?.common?.is_socket_opened) {
                try {
                    await this.waitForEmbedBalance(15000);
                } catch (err) {
                    // eslint-disable-next-line no-console
                    console.warn('[Auth] Embed balance resubscribe failed:', err);
                }
                return;
            }
            window.__DTRADER_EMBED_OTP_BUSY = true;
            window.__DTRADER_EMBED_AUTH_PENDING = true;
            try {
                const loginid =
                    sessionStorage.getItem('active_loginid') || localStorage.getItem('active_loginid') || '';
                if (!loginid) return;
                const ws_url = await fetchOTP(loginid);
                BinarySocket.setWSUrl(ws_url);
                BinarySocket.closeAndOpenNewConnection();
                await this.waitForEmbedBalance(45000);
                this.setIsAuthorize(true);
                BinarySocket.sendBuffered();
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error('[Auth] Embed-mode v4 OTP reconnect failed:', err);
            } finally {
                window.__DTRADER_EMBED_AUTH_PENDING = false;
                window.__DTRADER_EMBED_OTP_BUSY = false;
            }
        };

        if (typeof BinarySocket.setOnReconnect === 'function') {
            BinarySocket.setOnReconnect(reconnectEmbedOtp);
        }

        const otpPromise = (async () => {
            try {
                const accounts = await fetchAccounts();
                const preferred_loginid =
                    sessionStorage.getItem('active_loginid') || localStorage.getItem('active_loginid') || '';
                const active_account =
                    accounts.find(a => a.account_id === preferred_loginid) ||
                    accounts.find(a => a.account_type === 'demo') ||
                    accounts[0];

                if (!active_account) throw new Error('No accounts found for embed OAuth session');

                sessionStorage.setItem('active_loginid', active_account.account_id);
                localStorage.setItem('active_loginid', active_account.account_id);
                localStorage.setItem('account_type', active_account.account_type);

                const ws_url = await fetchOTP(active_account.account_id);
                BinarySocket.setWSUrl(ws_url);
                BinarySocket.closeAndOpenNewConnection();

                await this.waitForEmbedBalance(45000);
                this.setIsAuthorize(true);
                BinarySocket.sendBuffered();

                // eslint-disable-next-line no-console
                console.log(
                    '[Auth] Embed-mode v4 OTP OK (loginid: ' +
                        active_account.account_id.substring(0, 4) +
                        '…, app_id: ' +
                        (localStorage.getItem('config.app_id') || '?') +
                        ')',
                );
            } catch (error) {
                // Balance event may arrive before wait() resolves — keep OTP session alive.
                if (this.is_authorize) {
                    BinarySocket.sendBuffered();
                    return;
                }
                throw error;
            } finally {
                window.__DTRADER_EMBED_AUTH_PENDING = false;
                window.__DTRADER_EMBED_OTP_BUSY = false;
                window.__DTRADER_EMBED_OTP_PROMISE = null;
            }
        })();

        window.__DTRADER_EMBED_OTP_PROMISE = otpPromise;
        return otpPromise;
    }

    /**
     * Light OTP reconnect for embed — used on AuthorizationRequired without full client.init().
     */
    async reconnectEmbedOAuthSocket() {
        if (window.__DTRADER_EMBED_OTP_BUSY) {
            if (window.__DTRADER_EMBED_OTP_PROMISE) {
                return window.__DTRADER_EMBED_OTP_PROMISE;
            }
            return;
        }
        if (this.is_authorize && this.root_store?.common?.is_socket_opened) {
            try {
                await this.waitForEmbedBalance(15000);
                BinarySocket.sendBuffered();
            } catch (_) {
                /* ignore */
            }
            return;
        }
        window.__DTRADER_EMBED_OTP_BUSY = true;
        window.__DTRADER_EMBED_AUTH_PENDING = true;
        try {
            const loginid =
                sessionStorage.getItem('active_loginid') || localStorage.getItem('active_loginid') || '';
            if (!loginid) return;

            const ws_url = await fetchOTP(loginid);
            BinarySocket.setWSUrl(ws_url);
            BinarySocket.closeAndOpenNewConnection();
            await this.waitForEmbedBalance(45000);
            this.setIsAuthorize(true);
            BinarySocket.sendBuffered();
            if (this.root_store?.common?.has_error) {
                this.root_store.common.setError(false, null);
            }
            this.root_store?.common?.resetServicesError?.();
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('[Auth] Embed OTP reconnect failed:', err);
            if (this.is_authorize) {
                try {
                    BinarySocket.send({ balance: 1, subscribe: 1 });
                } catch (_) {
                    /* ignore */
                }
            }
        } finally {
            window.__DTRADER_EMBED_AUTH_PENDING = false;
            window.__DTRADER_EMBED_OTP_BUSY = false;
        }
    }

    async initEmbedAuth(token) {
        const app_id = (() => {
            try {
                const override = window.localStorage.getItem('config.app_id');
                if (override && /^\d+$/.test(override)) return override;
            } catch (_) {
                // ignore
            }
            // Fallback to brand-default — caller has already verified token
            // exists, so we have to send SOMETHING; the parent should always
            // forward its own app_id but we don't want to throw if it didn't.
            return '16929';
        })();

        const lang = (getInitialLanguage() || 'EN').toLowerCase();

        const ws_url = `wss://ws.derivws.com/websockets/v3?app_id=${encodeURIComponent(app_id)}&l=${encodeURIComponent(lang)}&brand=deriv`;

        // Tell socket_base.js: don't auto-subscribe to balance on this open —
        // we will, ourselves, after authorize succeeds. The "_ACTIVE" flag is
        // set for the WHOLE embed session so reconnects also defer to our
        // reconnect handler for re-authorize-then-subscribe ordering.
        window.__DTRADER_EMBED_AUTH_PENDING = true;
        window.__DTRADER_EMBED_MODE_ACTIVE = true;

        try {
            BinarySocket.setWSUrl(ws_url);
            BinarySocket.closeAndOpenNewConnection();

            // deriv-api buffers this until the WS opens, then sends it as the
            // first message — so it always reaches the server before any
            // unrelated request that might have been queued elsewhere.
            const response = await BinarySocket.send({ authorize: token });

            if (response && response.error) {
                throw new Error(
                    `authorize rejected: ${response.error.code || 'unknown'} — ${
                        response.error.message || 'no message'
                    }`,
                );
            }

            const authorize = response && response.authorize;
            if (!authorize || !authorize.loginid) {
                throw new Error('authorize response missing loginid');
            }

            // Persist the active account so the rest of the app (and any
            // subsequent visibility-listener checks) sees a coherent session.
            try {
                sessionStorage.setItem('active_loginid', authorize.loginid);
                localStorage.setItem('active_loginid', authorize.loginid);
                localStorage.setItem('account_type', /^VRT/.test(authorize.loginid) ? 'demo' : 'real');
            } catch (_) {
                // private mode etc. — non-fatal
            }

            // Hydrate the MobX store from the authorize response and flip the
            // is_authorize flag so the rest of the UI re-renders.
            this.responseAuthorize(response);
            this.setIsAuthorize(true);

            // Now that we are authorized, start streaming balance updates.
            // socket-general.js will route them to setBalanceActiveAccount.
            BinarySocket.send({ balance: 1, subscribe: 1 });

            // eslint-disable-next-line no-console
            console.log(
                '[Auth] Embed-mode v3 authorize OK (loginid: ' +
                    authorize.loginid.substring(0, 4) +
                    '…, balance: ' +
                    (authorize.balance ?? '?') +
                    ' ' +
                    (authorize.currency || '') +
                    ', app_id: ' +
                    app_id +
                    ')',
            );

            // Register a reconnect handler so the session survives transient
            // socket drops. Without this, the next onOpen would auto-subscribe
            // to balance with no prior authorize and the server would reply
            // AuthorizationRequired → socket-general logs the user out.
            // We re-authorize first, then re-subscribe to balance ourselves.
            // setOnReconnect dedupes by function reference, so re-running this
            // path on a manual reload does not stack handlers.
            const reauthorize = () => {
                try {
                    window.__DTRADER_EMBED_AUTH_PENDING = true;
                    BinarySocket.send({ authorize: token })
                        .then(reauthResponse => {
                            if (reauthResponse?.error) {
                                // eslint-disable-next-line no-console
                                console.error(
                                    '[Auth] Embed-mode reconnect authorize rejected:',
                                    reauthResponse.error,
                                );
                                return;
                            }
                            BinarySocket.send({ balance: 1, subscribe: 1 });
                        })
                        .catch(err => {
                            // eslint-disable-next-line no-console
                            console.error('[Auth] Embed-mode reconnect authorize threw:', err);
                        })
                        .finally(() => {
                            window.__DTRADER_EMBED_AUTH_PENDING = false;
                        });
                } catch (err) {
                    // eslint-disable-next-line no-console
                    console.error('[Auth] Embed-mode reconnect handler threw:', err);
                }
            };
            if (typeof BinarySocket.setOnReconnect === 'function') {
                BinarySocket.setOnReconnect(reauthorize);
            }

            return response;
        } finally {
            // Always clear the flag so future reconnects use the normal path.
            window.__DTRADER_EMBED_AUTH_PENDING = false;
        }
    }

    setLoginId(loginid) {
        this.loginid = loginid;
    }

    setIsLoggingIn(bool) {
        this.is_logging_in = bool;
    }

    setBalanceActiveAccount(obj_balance) {
        if (!obj_balance || typeof obj_balance.balance === 'undefined') {
            return;
        }

        if (this.current_account && obj_balance.loginid === this.current_account.loginid) {
            this.current_account.balance = obj_balance.balance;

            if (this.is_virtual) {
                this.root_store.notifications.resetVirtualBalanceNotification(this.current_account.loginid);
            }

            // Update localStorage
            localStorage.setItem(storage_key, JSON.stringify(this.current_account));
        }
    }

    selectCurrency(value) {
        this.selected_currency = value;
    }

    setEmail(email) {
        if (this.current_account) {
            this.current_account.email = email;
            this.email = email;
        }
    }

    setIsClientStoreInitialized() {
        this.is_client_store_initialized = true;
    }

    setInitialized(is_initialized) {
        this.initialized_broadcast = is_initialized;
    }

    async cleanUp() {
        // Remove visibility listener
        this.removeVisibilityListener();

        // Clean up notifications
        const notification_messages = LocalStore.getObject('notification_messages');
        if (notification_messages && this.loginid) {
            delete notification_messages[this.loginid];
            LocalStore.setObject('notification_messages', { ...notification_messages });
        }

        // Reset state
        this.loginid = null;
        this.user_id = null;
        this.external_id = null;
        this.current_account = null;

        LocalStore.set('marked_notifications', JSON.stringify([]));
        // Remove keys instead of writing the string "null" (localStorage stringifies null).
        localStorage.removeItem('active_loginid');
        sessionStorage.removeItem('active_loginid');
        localStorage.removeItem('active_user_id');
        localStorage.removeItem(storage_key);

        // Clear OAuth tokens and reset WS to public endpoint
        clearTokens();
        BinarySocket.setWSUrl(null);

        runInAction(() => {
            // Since payout_currencies endpoint has been removed, use USD as default
            this.responsePayoutCurrencies(['USD']);
        });
        this.root_store.notifications.removeAllNotificationMessages(true);

        // Drop WebSocket connection and reconnect to public endpoint
        BinarySocket.closeAndOpenNewConnection();
    }

    setShouldRedirectToLogin(should_redirect_user_to_login) {
        this.should_redirect_user_to_login = should_redirect_user_to_login;
    }

    async logout() {
        const response = await requestRestLogout();

        if (response?.logout === 1) {
            await this.cleanUp();
            this.setLogout(true);
        }

        return response;
    }

    setLogout(is_logged_out) {
        this.has_logged_out = is_logged_out;
        if (this.root_store.common.has_error) this.root_store.common.setError(false, null);
    }

    removeTokenFromUrl() {
        const url = new URL(window.location.href);
        if (url.searchParams.has('token')) {
            url.searchParams.delete('token');
            window.history.replaceState({}, document.title, url.toString());
        }
    }

    /**
     * Switch to a different account.
     * Updates loginid, fetches a fresh OTP, and reconnects the WebSocket.
     * @param {string} account_id - The account ID to switch to
     */
    async switchAccount(account_id) {
        if (!account_id || this.loginid === account_id) return;

        // Track the newly active account for multi-tab sync
        localStorage.setItem('active_loginid', account_id);
        sessionStorage.setItem('active_loginid', account_id);

        // Update account_type so is_virtual computed reflects the switched account immediately.
        // DEM-prefixed IDs are demo accounts; all others are real.
        const switched_account_type = account_id.startsWith('DEM') ? 'demo' : 'real';
        localStorage.setItem('account_type', switched_account_type);

        // Update the store's loginid immediately so components don't render stale data
        // while waiting for the balance response to arrive.
        this.setLoginId(account_id);

        // Clear notifications when switching accounts (similar to old implementation)
        this.root_store.notifications.removeNotifications(true);
        this.root_store.notifications.removeTradeNotifications();
        this.root_store.notifications.removeAllNotificationMessages(true);

        // Clear contract markers to prevent showing previous account's contracts on chart
        this.root_store.contract_trade.clearContracts();

        // Fetch a fresh OTP for the new account — OTP URLs are single-use and
        // embed the account ID, so reusing the old URL would connect to the wrong account.
        try {
            const ws_url = await fetchOTP(account_id);
            BinarySocket.setWSUrl(ws_url);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('[Auth] Failed to fetch OTP for account switch:', error);
        }

        BinarySocket.closeAndOpenNewConnection();
    }
}
